<template>
  <div class="vendor_print_check-main glass-tab-page vendorprintcheck-container">
    <div class="vendor_print_check-header-bar">
      <BrucdcumHeader :breadcrumb="['Banking', 'Print Check']" />
    </div>

    <form class="vendor_print_check-form" @submit.prevent>
      <div class="glass-tab-view glass-tab-view--flush vendor_print_check-glass-shell">
        <div class="glass-tab-view__content vendor_print_check-tab-content">
          <div class="vendor_print_check-main-layout vendorprintcheck-main-layout">
            <!-- Left Column: Selection -->
            <div class="vendor_print_check-left-column vendorprintcheck-left-column">
              <div class="vendorprintcheck-section glass-tab-panel glass-tab-panel--flex-column">
                <h3 class="glass-tab-panel__title vendorprintcheck-section-title">Select Vendor</h3>
                <div class="vendorprintcheck-vendor-selector">
                  <LabelDropdown
                    label="Vendor"
                    select-id="vendor-print-check-vendor"
                    v-model="selectedVendor"
                    flex
                    wide
                    searchable
                    :options="vendorDropdownOptions"
                    @change="onVendorChange"
                  />
                </div>
              </div>

              <div
                v-if="selectedVendor"
                class="vendorprintcheck-section glass-tab-panel glass-tab-panel--flex-column"
              >
                <h3 class="glass-tab-panel__title vendorprintcheck-section-title">Select Checks to Print</h3>
                <div v-if="loading" class="vendorprintcheck-loading">Loading pay bills...</div>
                <div
                  v-if="inlineErrorMessage"
                  class="vendorprintcheck-error vendor_print_check-alert glass-tab-panel__alert glass-tab-panel__alert--error"
                >
                  {{ inlineErrorMessage }}
                </div>

                <div class="vendor_print_check-batch-controls vendorprintcheck-batch-controls" v-if="payBills.length > 0">
                  <SecondaryButton
                    :label="`Select All (${payBills.length})`"
                    icon="bi bi-check2-all"
                    compact
                    :disabled="selectedBillIds.length === payBills.length"
                    @click="selectAllChecks"
                  />
                  <SecondaryButton
                    label="Clear All"
                    icon="bi bi-x-lg"
                    compact
                    :disabled="selectedBillIds.length === 0"
                    @click="clearAllSelections"
                  />
                  <span class="vendor_print_check-selection-count vendorprintcheck-selection-count">
                    {{ selectedBillIds.length }} check(s) selected
                  </span>
                </div>

                <div v-if="payBills.length > 0" class="consultant-glass-table-section vendor_print_check-table-section">
                  <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
                    <div class="consultant-glass-table-scroll-x">
                      <table class="consultant-glass-table vendorprintcheck-table vendorprintcheck-simple-table">
        <thead>
          <tr>
                <th class="vendorprintcheck-checkbox-col">
                  <input 
                    type="checkbox" 
                    :checked="isAllSelected"
                    @change="toggleSelectAll"
                    class="vendorprintcheck-checkbox"
                  />
                </th>
                <th>Pay Bill #</th>
            <th>Date</th>
                <th>Total Amount</th>
          </tr>
        </thead>
        <tbody>
              <tr
                v-for="bill in payBills"
                :key="bill.id"
                class="consultant-glass-table-row"
                :class="{ selected: selectedBillIds.includes(bill.id) }"
              >
                <td class="vendorprintcheck-checkbox-col">
                  <input 
                    type="checkbox" 
                    :value="bill.id"
                    v-model="selectedBillIds" 
                    @change="onCheckSelection"
                    :disabled="!canPrint(bill)"
                    class="vendorprintcheck-checkbox"
                  />
                </td>
                <td>{{ bill.billNumber }}</td>
                <td>{{ formatDateToUS(bill.billDate) }}</td>
                <td>{{ formatCurrency(bill.billAmount, false) }}</td>
          </tr>
        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div
                  v-if="payBills.length === 0 && selectedVendor && !loading && !inlineErrorMessage"
                  class="vendorprintcheck-no-checks"
                >
                  No checks ready for printing found for this vendor.
                </div>
              </div>
            </div>

            <!-- Right Column: Check Layout Preview -->
            <div
              v-if="selectedPayBills.length > 0"
              class="vendor_print_check-right-column vendorprintcheck-right-column"
            >
              <div class="vendorprintcheck-section glass-tab-panel glass-tab-panel--flex-column">
                <h3 class="glass-tab-panel__title vendorprintcheck-section-title">Print Options</h3>

                <div class="vendor_print_check-options-grid vendorprintcheck-options-grid">
                  <div class="vendorprintcheck-option-group vendorprintcheck-option-group--label-dropdown">
                    <div class="vendor_print_check-printer-controls vendorprintcheck-printer-controls">
                      <LabelDropdown
                        label="Printer(s)"
                        select-id="vendor-print-check-printer"
                        v-model="selectedPrinter"
                        flex
                        wide
                        :options="printerDropdownOptions"
                        @change="onPrinterChange"
                      />
                      <SecondaryButton
                        icon="bi bi-arrow-clockwise"
                        label="Refresh"
                        compact
                        :disabled="loadingPrinters"
                        :loading="loadingPrinters"
                        title="Refresh printer list"
                        @click="refreshPrinters"
                      />
                    </div>
                    <div v-if="printerLoadMethod" class="vendorprintcheck-printer-info">
                      <small>{{ printerLoadMethod }}</small>
                    </div>
                    <div v-if="availablePrinters.length === 0 && !loadingPrinters" class="vendorprintcheck-no-printers">
                      <small>No real printers detected. Please connect a printer and refresh.</small>
                    </div>
                    <div v-if="isPrinting" class="vendorprintcheck-print-status">
                      <div class="print-status-indicator">
                        <span class="print-spinner"><i class="bi bi-arrow-repeat"></i></span>
                        <span class="print-method">{{ currentPrintMethod }}</span>
                        <span class="print-message">{{ printStatus }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="vendorprintcheck-option-group vendorprintcheck-option-group--label-dropdown">
                    <LabelDropdown
                      label="Check Style"
                      select-id="vendor-print-check-style"
                      v-model="checkStyle"
                      flex
                      wide
                      :options="checkStyleDropdownOptions"
                      @change="onCheckStyleChange"
                    />
                  </div>
        
        
        <!-- Pre-printed Check Specific Options -->
        <div class="vendorprintcheck-option-group">
          <label class="vendorprintcheck-option-label">Pre-printed Check Settings</label>
          <div class="vendorprintcheck-preprinted-controls">
            <div class="preprinted-info">
              <small>ℹ️ This will print only the stubs. The check portion should be pre-printed with your company details.</small>
            </div>
          </div>
        </div>
          </div>
          
        </div>

        <!-- Remittance Advice Field Controls -->
        <div class="vendorprintcheck-section glass-tab-panel glass-tab-panel--flex-column">
          <div class="vendor_print_check-section-header vendorprintcheck-section-header" @click="toggleRemittanceAdvice">
            <h3 class="glass-tab-panel__title vendorprintcheck-section-title">Remittance Advice Stub Fields</h3>
            <button type="button" class="expand-collapse-btn">
              {{ remittanceAdviceExpanded ? '▼' : '▶' }}
            </button>
          </div>
          
          <div v-show="remittanceAdviceExpanded" class="vendorprintcheck-section-content">
            <div class="field-controls-grid">

            <div class="field-control-group">
              <h5>Vendor Information</h5>
              <label class="field-toggle">
                <input type="checkbox" v-model="stubFieldVisibility.vendorName">
                Vendor Name
              </label>
              <label class="field-toggle">
                <input type="checkbox" v-model="stubFieldVisibility.vendorPhone">
                Phone
              </label>
              <label class="field-toggle">
                <input type="checkbox" v-model="stubFieldVisibility.vendorAddress">
                Address
              </label>
              </div>

            <div class="field-control-group">
              <h5>Bill Details Table</h5>
              <label class="field-toggle">
                <input type="checkbox" v-model="stubFieldVisibility.billNumber">
                Bill Number
              </label>
              <label class="field-toggle">
                <input type="checkbox" v-model="stubFieldVisibility.billDate">
                Bill Date
              </label>
              <label class="field-toggle">
                <input type="checkbox" v-model="stubFieldVisibility.billAmount">
                Bill Amount
              </label>
              <label class="field-toggle">
                <input type="checkbox" v-model="stubFieldVisibility.amountPaid">
                Amount Paid
              </label>
              <label class="field-toggle">
                <input type="checkbox" v-model="stubFieldVisibility.description">
                Description
              </label>
              <label class="field-toggle">
                <input type="checkbox" v-model="stubFieldVisibility.totalPayment">
                Total Payment
              </label>
                    </div>
                    </div>
          <div class="stub-title-control">
            <h5>Stub Titles</h5>
            <label class="field-toggle">
              <input type="checkbox" v-model="showStubTitles">
              Show "REMITTANCE ADVICE" Titles
            </label>
                  </div>
          <div class="field-controls-actions">
            <SecondaryButton label="Select All" icon="bi bi-check2-all" compact @click="selectAllFields" />
            <SecondaryButton label="Deselect All" icon="bi bi-x-lg" compact @click="deselectAllFields" />
            <SecondaryButton label="Reset Defaults" icon="bi bi-arrow-counterclockwise" compact @click="resetFieldDefaults" />
                </div>
            </div>
          </div>

        <div class="vendorprintcheck-section glass-tab-panel glass-tab-panel--flex-column">
          <div class="vendor_print_check-section-header vendorprintcheck-section-header" @click="toggleCheckLayout">
            <h3 class="glass-tab-panel__title vendorprintcheck-section-title">Check Layout Preview</h3>
            <button type="button" class="expand-collapse-btn">
              {{ checkLayoutExpanded ? '▼' : '▶' }}
            </button>
          </div>
          
          <div v-show="checkLayoutExpanded" class="vendorprintcheck-section-content">
            <!-- Drag and Drop Controls -->
            <div class="drag-controls">
              <span class="drag-instructions">
                💡 Drag Amount, Date, Vendor Name, Memo, and Amount in Words to match your pre-printed check layout.
              </span>
                </div>

          <!-- All Field Positions Overview -->
                  <div class="all-positions-panel">
                    <div class="positions-header">
                      <h5>📋 All Field Positions</h5>
                      <div class="positions-actions">
                        <button @click="resetAllToDefaults" class="action-btn reset-btn">
                          🔄 Reset All
                        </button>
                        <button @click="toggleEditMode" class="action-btn edit-btn">
                          {{ editMode ? '✅ Done Editing' : '✏️ Edit Fields' }}
                        </button>
                      </div>
                    </div>
                    
                    <div class="positions-grid">
                      <div class="position-item" 
                           v-for="(field, fieldName) in fieldAdjustments" 
                           :key="fieldName"
                           :class="{ 'editing': editMode }">
                        <div class="position-header">
                          <span class="field-icon">{{ getFieldIcon(fieldName) }}</span>
                          <span class="field-name">{{ getFieldDisplayName(fieldName) }}</span>
                          <button v-if="editMode" @click="selectElement(fieldName)" class="select-field-btn">
                            {{ selectedElement === fieldName ? '✓' : 'Select' }}
                          </button>
                        </div>
                        
                        <div class="position-details">
                          <div class="position-row">
                            <span class="position-label">Position:</span>
                            <div class="position-inputs" v-if="editMode">
                              <input type="number" v-model.number="field.x" min="0" max="600" class="position-input">
                              <span class="px-label">px</span>
                              <input type="number" v-model.number="field.y" min="0" max="280" class="position-input">
                              <span class="px-label">px</span>
                            </div>
                            <span v-else class="position-value">{{ field.x }}px, {{ field.y }}px</span>
                          </div>
                          
                          <div class="position-row">
                            <span class="position-label">Size:</span>
                            <div class="position-inputs" v-if="editMode">
                              <input type="number" v-model.number="field.width" :min="getFieldMinWidth(fieldName)" :max="getFieldMaxWidth(fieldName)" class="position-input">
                              <span class="px-label">×</span>
                              <input type="number" v-model.number="field.height" min="15" max="50" class="position-input">
                              <span class="px-label">px</span>
                            </div>
                            <span v-else class="position-value">{{ field.width }}px × {{ field.height }}px</span>
                          </div>
                          
                          <div class="position-row">
                            <span class="position-label">Font:</span>
                            <div class="position-inputs" v-if="editMode">
                              <input type="number" v-model.number="field.fontSize" min="8" max="24" class="position-input">
                              <span class="px-label">px</span>
                            </div>
                            <span v-else class="position-value">{{ field.fontSize }}px</span>
                          </div>
                        </div>
                        
                        <div v-if="editMode" class="field-actions">
                          <button @click="resetFieldToDefault(fieldName)" class="mini-btn reset-mini-btn">
                            Reset
                          </button>
                          <button @click="fitFieldToContent(fieldName)" class="mini-btn fit-mini-btn">
                            Auto Fit
                          </button>
                          <button @click="centerFieldHorizontally(fieldName)" class="mini-btn center-mini-btn">
                            Center
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    <div class="save-controls">
                      <button 
                        @click="saveOrUpdateAdjustments" 
                        class="save-update-btn"
                        :class="{ 'update-btn': hasAdjustmentsInDB, 'save-btn': !hasAdjustmentsInDB }"
                      >
                        {{ hasAdjustmentsInDB ? '🔄 Update Adjustments' : '💾 Save Adjustments' }}
                      </button>
                      <span class="save-status">
                        {{ hasAdjustmentsInDB ? 'Adjustments exist in database' : 'No adjustments saved yet' }}
                      </span>
                    </div>
                  </div>
                  </div>

          <div class="check-preview-container" :class="previewClasses" v-for="bill in selectedPayBills" :key="bill.id" :style="{ width: checkStyleConfig.width, minHeight: checkStyleConfig.height }">
            <!-- Pre-printed Check Layout with Draggable Elements -->
              <div class="cheque-section cheque-preprinted-check">
                <!-- Background placeholder for pre-printed check -->
                <div class="preprinted-check-background">
                  <div class="check-background-placeholder">
              </div>
              </div>
          
                <!-- Draggable Elements - Only Values -->
                <div class="draggable-amount"
                     :style="getElementStyle('amount')"
                     @mousedown="startDrag($event, 'amount')"
                     @touchstart="startDrag($event, 'amount')"
                     @click="selectElement('amount')">
                  {{ formatCurrency(bill.billAmount, false) }}
              </div>
          
                <div class="draggable-payee"
                     :style="getElementStyle('payee')"
                     @mousedown="startDrag($event, 'payee')"
                     @touchstart="startDrag($event, 'payee')"
                     @click="selectElement('payee')">
                  {{ getVendorName(bill) }}
              </div>
          
                <div class="draggable-date"
                     :style="getElementStyle('date')"
                     @mousedown="startDrag($event, 'date')"
                     @touchstart="startDrag($event, 'date')"
                     @click="selectElement('date')">
                  {{ getCurrentDateUS() }}
              </div>
          
                <div class="draggable-memo"
                     :style="getElementStyle('memo')"
                     @mousedown="startDrag($event, 'memo')"
                     @touchstart="startDrag($event, 'memo')"
                     @click="selectElement('memo')">
                  {{ getCheckMemo(bill) }}
              </div>

                <div class="draggable-amount-words"
                     :style="getElementStyle('amountInWords')"
                     @mousedown="startDrag($event, 'amountInWords')"
                     @touchstart="startDrag($event, 'amountInWords')"
                     @click="selectElement('amountInWords')">
                  {{ numberToWords(bill.billAmount) }}
              </div>
            </div>

              <!-- First Stub (Vendor Copy) -->
              <div class="cheque-section cheque-stub" v-if="checkStyleConfig.showStubs">
          <div class="stub-header">
                  <h4 v-if="showStubTitles">REMITTANCE ADVICE (First Stub – Vendor Copy)</h4>
                  <div class="stub-info" v-if="stubFieldVisibility.checkNumber || stubFieldVisibility.date || stubFieldVisibility.amount">
                    <span v-if="stubFieldVisibility.checkNumber">Check #: {{ getCheckNumberForBill() }}</span>
                    <span v-if="stubFieldVisibility.date">Date: {{ getCurrentDateUS() }}</span>
                    <span v-if="stubFieldVisibility.amount">Amount: {{ formatCurrency(bill.billAmount) }}</span>
                  </div>
                </div>
                <div class="stub-content">
                  <div class="stub-vendor-info" v-if="stubFieldVisibility.vendorName || stubFieldVisibility.vendorPhone || stubFieldVisibility.vendorAddress">
                    <div class="vendor-details-left">
                      <div v-if="stubFieldVisibility.vendorName"><strong>Vendor: {{ getVendorName(bill) }}</strong></div>
                      <div v-if="stubFieldVisibility.vendorPhone && bill.phone">Phone: {{ bill.phone }}</div>
                    </div>
                    <div class="vendor-details-right" v-if="stubFieldVisibility.vendorAddress">
                      <div><strong>Address:</strong></div>
                      <div>{{ getVendorAddress() }}</div>
                    </div>
                  </div>
                  <div class="stub-bills-table" v-if="bill.records && bill.records.length > 0">
                    <table>
                      <thead>
                        <tr>
                          <th v-if="stubFieldVisibility.billNumber">Bill Number</th>
                          <th v-if="stubFieldVisibility.billDate">Bill Date</th>
                          <th v-if="stubFieldVisibility.billAmount">Bill Amount</th>
                          <th v-if="stubFieldVisibility.amountPaid">Amount Paid</th>
                          <th v-if="stubFieldVisibility.description">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="record in bill.records" :key="record.id">
                          <td v-if="stubFieldVisibility.billNumber">{{ cleanBillNumber(record.bill?.billNo) || `Bill #${record.billId}` }}</td>
                          <td v-if="stubFieldVisibility.billDate">{{ formatDateToUS(record.bill?.date) }}</td>
                          <td v-if="stubFieldVisibility.billAmount">{{ formatCurrency(record.bill?.total) }}</td>
                          <td v-if="stubFieldVisibility.amountPaid">{{ formatCurrency(record.amountPaid) }}</td>
                          <td v-if="stubFieldVisibility.description">{{ record.bill?.comments || bill.comments || '-' }}</td>
                        </tr>
                      </tbody>
                      <tfoot v-if="stubFieldVisibility.totalPayment">
                        <tr>
                          <td :colspan="getVisibleColumnsCount()"><strong>Total Payment:</strong></td>
                          <td v-if="stubFieldVisibility.amountPaid"><strong>{{ formatCurrency(bill.billAmount) }}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
            </div>
          </div>

              <!-- Second Stub (Company Copy) -->
              <div class="cheque-section cheque-stub" v-if="checkStyleConfig.showStubs && checkStyleConfig.stubCount >= 2">
                <div class="stub-header">
                  <h4 v-if="showStubTitles">REMITTANCE ADVICE (Second Stub – Company Copy)</h4>
                </div>
                <div class="stub-content">
                  <div class="stub-vendor-info" v-if="stubFieldVisibility.vendorName || stubFieldVisibility.vendorPhone || stubFieldVisibility.vendorAddress">
            <div class="vendor-details-left">
                      <div v-if="stubFieldVisibility.vendorName"><strong>Vendor: {{ getVendorName(bill) }}</strong></div>
                      <div v-if="stubFieldVisibility.vendorPhone && bill.phone">Phone: {{ bill.phone }}</div>
            </div>
                    <div class="vendor-details-right" v-if="stubFieldVisibility.vendorAddress">
              <div><strong>Address:</strong></div>
              <div>{{ getVendorAddress() }}</div>
            </div>
          </div>
                  <div class="stub-bills-table" v-if="bill.records && bill.records.length > 0">
                    <table>
                      <thead>
                        <tr>
                          <th v-if="stubFieldVisibility.billNumber">Bill Number</th>
                          <th v-if="stubFieldVisibility.billDate">Bill Date</th>
                          <th v-if="stubFieldVisibility.billAmount">Bill Amount</th>
                          <th v-if="stubFieldVisibility.amountPaid">Amount Paid</th>
                          <th v-if="stubFieldVisibility.description">Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="record in bill.records" :key="record.id">
                          <td v-if="stubFieldVisibility.billNumber">{{ cleanBillNumber(record.bill?.billNo) || `Bill #${record.billId}` }}</td>
                          <td v-if="stubFieldVisibility.billDate">{{ formatDateToUS(record.bill?.date) }}</td>
                          <td v-if="stubFieldVisibility.billAmount">{{ formatCurrency(record.bill?.total) }}</td>
                          <td v-if="stubFieldVisibility.amountPaid">{{ formatCurrency(record.amountPaid) }}</td>
                          <td v-if="stubFieldVisibility.description">{{ record.bill?.comments || bill.comments || '-' }}</td>
                        </tr>
                      </tbody>
                      <tfoot v-if="stubFieldVisibility.totalPayment">
                        <tr>
                          <td :colspan="getVisibleColumnsCount()"><strong>Total Payment:</strong></td>
                          <td v-if="stubFieldVisibility.amountPaid"><strong>{{ formatCurrency(bill.billAmount) }}</strong></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
            </div>
          </div>

          <!-- Print History Section -->
          <div
            v-if="printHistory.length > 0"
            class="vendorprintcheck-section glass-tab-panel glass-tab-panel--flex-column"
          >
            <h3 class="glass-tab-panel__title vendorprintcheck-section-title">Recent Print History</h3>
            <div class="vendor_print_check-history-controls vendorprintcheck-history-controls">
              <SecondaryButton
                label="Refresh History"
                icon="bi bi-arrow-clockwise"
                compact
                :disabled="loadingHistory"
                :loading="loadingHistory"
                @click="loadPrintHistory"
              />
              <span class="vendorprintcheck-history-count">
                {{ printHistory.length }} recent print(s)
              </span>
            </div>

            <div class="consultant-glass-table-section vendor_print_check-table-section">
              <div class="consultant-glass-table-responsive consultant-glass-table-responsive--framed">
                <div class="consultant-glass-table-scroll-x">
                  <table class="consultant-glass-table vendorprintcheck-table vendorprintcheck-history-table">
          <thead>
            <tr>
              <th>Check #</th>
              <th>Pay Bill #</th>
              <th>Vendor</th>
              <th>Amount</th>
              <th>Printed Date</th>
              <th>Printer</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="historyItem in printHistory" :key="historyItem.id" class="consultant-glass-table-row">
              <td>{{ historyItem.checkNumber || 'N/A' }}</td>
              <td>{{ historyItem.billNumber }}</td>
              <td>{{ historyItem.vendorName }}</td>
              <td>{{ formatCurrency(historyItem.amount) }}</td>
              <td>{{ formatDateToUS(historyItem.printedAt) }}</td>
              <td>{{ historyItem.printerName || 'Unknown' }}</td>
              <td>
                <span class="vendorprintcheck-status" :class="getHistoryStatusClass(historyItem)">
                  {{ getHistoryStatusText(historyItem) }}
                </span>
              </td>
              <td>
                <SecondaryButton
                  label="Void"
                  icon="bi bi-x-circle"
                  compact
                  custom-class="consultant-secondary-btn--danger"
                  title="Void this check"
                  :disabled="historyItem.isVoided"
                  @click="voidCheck(historyItem)"
                />
              </td>
            </tr>
          </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="selectedPayBills.length > 0"
        class="vendor_print_check-form-actions glass-tab-view__actions vendorprintcheck-actions"
      >
        <div class="vendor_print_check-print-summary vendorprintcheck-print-summary">
          <span class="vendorprintcheck-summary-text">
            {{ selectedPayBills.length }} check(s) selected • Total: {{ formatCurrency(totalSelectedAmount) }}
          </span>
        </div>
        <div class="vendor_print_check-action-buttons vendorprintcheck-action-buttons">
          <SecondaryButton label="Back" icon="bi bi-arrow-left" @click="goBack" />
          <PrimaryButton
            :label="printButtonLabel"
            icon="bi bi-printer"
            :disabled="!selectedPrinter || loading"
            :loading="loading"
            @click="printCheck"
          />
        </div>
      </div>
    </form>

    <!-- Success Modal -->
    <SuccessModal
      :show="showSuccessModal"
      :title="successTitle"
      :message="successMessage"
      @close="closeSuccessModal"
    />

    <!-- Error Modal -->
    <ErrorModal
      :show="showErrorModal"
      :title="errorTitle"
      :message="modalErrorMessage"
      @close="closeErrorModal"
    />

    <!-- Print Confirmation Modal -->
    <PrintJobCompletedModal
      :show="showPrintConfirmationModal"
      :printed-checks-count="printedChecksCount"
      :updating-status="updatingStatus"
      @close="closePrintConfirmationModal"
      @confirm-done="confirmPrintJobDone"
      @not-done="printJobNotDone"
    />
  </div>
</template>

<script>
import '../styles/VendorPrintCheck.css';
import BrucdcumHeader from '@/components/BrucdcumHeader.vue';
import logger from '@/utils/logger';
import SuccessModal from '@/components/SuccessModal.vue';
import ErrorModal from '@/components/ErrorModal.vue';
import PrintJobCompletedModal from '@/components/PrintJobCompletedModal.vue';
import ApiService from '@/services/api.service.js';
import CheckFieldAdjustmentService from '@/services/checkFieldAdjustment.service.js';
import LabelDropdown from '@/modules/consultant/components/LabelDropdown.vue';
import PrimaryButton from '@/modules/consultant/components/PrimaryButton.vue';
import SecondaryButton from '@/modules/consultant/components/SecondaryButton.vue';
import { isCheckPaymentMethod } from '../utils/vendorPaymentMethod.js';

export default {
  name: 'VendorPrintCheck',
  components: {
    BrucdcumHeader,
    SuccessModal,
    ErrorModal,
    PrintJobCompletedModal,
    LabelDropdown,
    PrimaryButton,
    SecondaryButton,
  },
  data() {
    return {
      selectedVendor: '',
      selectedBillId: '', // Keep for backward compatibility
      selectedBillIds: [], // New array for batch selection
      vendors: [],
      payBills: [],
      availablePrinters: [],
      selectedPrinter: '',
      loadingPrinters: false,
      printerLoadMethod: '',
      checkStyle: 'us-business-preprinted',
      checkPosition: 'top',
      // Pre-printed form detection
      isPrintingAllowed: false,
      // Drag and drop functionality
      isDragging: false,
      draggedElement: null,
      dragOffset: { x: 0, y: 0 },
      selectedElement: null,
      // Element positions (in pixels relative to the preview container)
      // Based on standard check layout dimensions (8.5" x 3.5")
      alignmentData: {
        amount: { x: 0, y: 0 },
        payee: { x: 0, y: 0 },
        date: { x: 0, y: 0 },
        memo: { x: 0, y: 0 },
        amountInWords: { x: 0, y: 0 }
      },
      // Remittance advice stub field visibility controls
      stubFieldVisibility: {
        vendorName: true,
        vendorPhone: true,
        vendorAddress: true,
        billNumber: true,
        billDate: true,
        billAmount: true,
        amountPaid: true,
        description: true,
        totalPayment: true
      },
      // Stub title visibility controls
      showStubTitles: true,
      // Print history
      printHistory: [],
      loadingHistory: false,
      bankInfo: {
        routingNumber: '123456789',
        accountNumber: '987654321'
      },
      loading: false,
      inlineErrorMessage: '',
      modalErrorMessage: '',
      showSuccessModal: false,
      successTitle: '',
      successMessage: '',
      showErrorModal: false,
      errorTitle: 'Error',
      // Print confirmation modal
      showPrintConfirmationModal: false,
      printedChecksCount: 0,
      printedBills: [],
      updatingStatus: false,
      // Print status tracking
      isPrinting: false,
      printStatus: '',
      currentPrintMethod: '',
      // Real-time warning tracking
      lastWarningTime: null,
      // Auto-save timeout
      saveTimeout: null,
      // Database tracking
      hasAdjustmentsInDB: false,
      // Edit mode for field positions
      editMode: false,
      // Expand/collapse states
      remittanceAdviceExpanded: true,
      checkLayoutExpanded: true,
      // Field specific adjustments
      fieldAdjustments: {
        date: {
          fontSize: 12,
          width: 120,
          height: 18,
          x: 450,
          y: 30
        },
        amount: {
          fontSize: 14,
          width: 120,
          height: 22,
          x: 480,
          y: 80
        },
        payee: {
          fontSize: 14,
          width: 350,
          height: 22,
          x: 120,
          y: 80
        },
        memo: {
          fontSize: 12,
          width: 250,
          height: 18,
          x: 80,
          y: 160
        },
        amountInWords: {
          fontSize: 12,
          width: 450,
          height: 18,
          x: 20,
          y: 110
        }
      }
    };
  },
  computed: {
    vendorDropdownOptions() {
      return [
        { value: '', label: 'Select Vendor' },
        ...(this.vendors || []).map((vendor) => ({
          value: String(vendor.id),
          label: vendor.name,
        })),
      ];
    },
    printerDropdownOptions() {
      return [
        { value: '', label: 'Select Printer' },
        ...(this.availablePrinters || []).map((printer) => ({
          value: String(printer.id),
          label: printer.name,
        })),
      ];
    },
    checkStyleDropdownOptions() {
      return [
        {
          value: 'us-business-preprinted',
          label: 'US Business Pre-printed Check (Stubs Only)',
        },
      ];
    },
    printButtonLabel() {
      if (this.selectedPayBills.length > 1) {
        return `Print ${this.selectedPayBills.length} Checks`;
      }
      return 'Print Check';
    },
    selectedPayBills() {
      // Support both single and batch selection
      if (this.selectedBillIds.length > 0) {
        return this.payBills.filter(bill => this.selectedBillIds.includes(bill.id));
      }
      return this.payBills.filter(bill => bill.id === this.selectedBillId);
    },
    isAllSelected() {
      return this.payBills.length > 0 && this.selectedBillIds.length === this.payBills.length;
    },
    totalSelectedAmount() {
      return this.selectedPayBills.reduce((sum, bill) => sum + parseFloat(bill.billAmount || 0), 0);
    },
    checkStyleConfig() {
      return {
          name: 'US Business Pre-printed Check (Stubs Only)',
          showStubs: true,
          stubCount: 2,
        checkPosition: 'top',
          width: '8.5in',
          height: '11in',
          isPreprinted: true
      };
    },
    previewClasses() {
      return [
        'vendorprintcheck-preview',
        `check-style-${this.checkStyle}`,
        `check-position-${this.checkStyleConfig.checkPosition}`,
        this.checkStyleConfig.showStubs ? 'with-stubs' : 'no-stubs'
      ];
    }
  },
  async mounted() {
    this.setupDragAndDropListeners();
    await this.loadInitialData();
    await this.loadPrinters();
    await this.loadPrintHistory();
    await this.loadFieldAdjustments();
  },
  beforeUnmount() {
    this.removeDragAndDropListeners();
    // Clear any pending save timeout
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
    }
  },
  watch: {
    // Auto-save coordinates when field visibility changes
    stubFieldVisibility: {
      handler() {
        // Field visibility changed
      },
      deep: true
    },
    // Auto-save coordinates when stub titles visibility changes
    showStubTitles() {
      // Stub titles visibility changed
    }
  },
  methods: {
    // Drag and Drop Methods
    setupDragAndDropListeners() {
      document.addEventListener('mousemove', this.handleDrag);
      document.addEventListener('mouseup', this.stopDrag);
      document.addEventListener('touchmove', this.handleDrag);
      document.addEventListener('touchend', this.stopDrag);
    },
    
    removeDragAndDropListeners() {
      document.removeEventListener('mousemove', this.handleDrag);
      document.removeEventListener('mouseup', this.stopDrag);
      document.removeEventListener('touchmove', this.handleDrag);
      document.removeEventListener('touchend', this.stopDrag);
    },
    
    startDrag(event, elementName) {
      event.preventDefault();
      event.stopPropagation();
      
      this.isDragging = true;
      this.draggedElement = elementName;
      this.selectedElement = elementName;
      
      const rect = event.currentTarget.getBoundingClientRect();
      
      if (event.type === 'mousedown') {
        this.dragOffset.x = event.clientX - rect.left;
        this.dragOffset.y = event.clientY - rect.top;
      } else if (event.type === 'touchstart') {
        this.dragOffset.x = event.touches[0].clientX - rect.left;
        this.dragOffset.y = event.touches[0].clientY - rect.top;
      }
    },
    
    handleDrag(event) {
      if (!this.isDragging || !this.draggedElement) return;
      
      event.preventDefault();
      
      const previewContainer = document.querySelector('.check-preview-container');
      if (!previewContainer) return;
      
      const previewRect = previewContainer.getBoundingClientRect();
      let clientX, clientY;
      
      if (event.type === 'mousemove') {
        clientX = event.clientX;
        clientY = event.clientY;
      } else if (event.type === 'touchmove') {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      }
      
      const newX = Math.max(0, Math.min(
        clientX - previewRect.left - this.dragOffset.x,
        previewRect.width - this.fieldAdjustments[this.draggedElement].width
      ));
      const newY = Math.max(0, Math.min(
        clientY - previewRect.top - this.dragOffset.y,
        previewRect.height - this.fieldAdjustments[this.draggedElement].height
      ));
      
      this.fieldAdjustments[this.draggedElement].x = newX;
      this.fieldAdjustments[this.draggedElement].y = newY;
      
      // Check for real-time positioning issues during drag
      this.checkRealTimePositioning();
      
    },
    
    stopDrag() {
      this.isDragging = false;
      this.draggedElement = null;
    },

    // Real-time positioning validation during drag
    checkRealTimePositioning() {
      if (!this.draggedElement) return;
      
      const currentPos = this.fieldAdjustments[this.draggedElement];
      const warnings = [];
      
      // Check if element is near edges (warning zone)
      const previewWidth = 600;
      const previewHeight = 280;
      const warningZone = 20; // 20px from edges
      
      if (currentPos.x < warningZone) {
        warnings.push(`⚠️ ${this.draggedElement} is too close to left edge`);
      }
      if (currentPos.x + currentPos.width > previewWidth - warningZone) {
        warnings.push(`⚠️ ${this.draggedElement} is too close to right edge`);
      }
      if (currentPos.y < warningZone) {
        warnings.push(`⚠️ ${this.draggedElement} is too close to top edge`);
      }
      if (currentPos.y + currentPos.height > previewHeight - warningZone) {
        warnings.push(`⚠️ ${this.draggedElement} is too close to bottom edge`);
      }
      
      // Check for overlapping with other elements
      const otherElements = Object.keys(this.fieldAdjustments).filter(el => el !== this.draggedElement);
      otherElements.forEach(otherEl => {
        const otherPos = this.fieldAdjustments[otherEl];
        if (this.isOverlapping(currentPos, otherPos)) {
          warnings.push(`⚠️ ${this.draggedElement} is overlapping with ${otherEl}`);
        }
      });
      
      // Show warning if there are issues (but not too frequently)
      if (warnings.length > 0 && !this.lastWarningTime) {
        this.lastWarningTime = Date.now();
        this.showRealTimeWarning(warnings);
      } else if (warnings.length === 0) {
        this.lastWarningTime = null;
      }
    },

    // Show real-time warning (with throttling)
    showRealTimeWarning(warnings) {
      // Throttle warnings to avoid spam (max 1 warning per 3 seconds)
      if (this.lastWarningTime && Date.now() - this.lastWarningTime < 3000) {
        return;
      }
      
      // Convert technical warnings to user-friendly messages
      const userFriendlyWarnings = warnings.map(warning => {
        if (warning.includes('too close to left edge')) {
          return '• Move element away from left edge';
        } else if (warning.includes('too close to right edge')) {
          return '• Move element away from right edge';
        } else if (warning.includes('too close to top edge')) {
          return '• Move element away from top edge';
        } else if (warning.includes('too close to bottom edge')) {
          return '• Move element away from bottom edge';
        } else if (warning.includes('overlapping')) {
          return '• Elements are overlapping - adjust positions';
        } else {
          return '• ' + warning.replace('⚠️ ', '');
        }
      });
      
      const warningMessage = `Layout Adjustment Needed:\n\n${userFriendlyWarnings.join('\n')}\n\nDrag the elements to better positions for optimal printing.`;
      
      // Use a less intrusive notification (you could also use toast notifications)
      logger.warn(warningMessage);
      
      // Optional: Show a brief visual indicator instead of alert
      this.showVisualWarning(userFriendlyWarnings);
    },

    // Show visual warning indicator
    showVisualWarning(warnings) {
      // Create a temporary warning indicator
      const warningDiv = document.createElement('div');
      warningDiv.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #fff3cd;
        border: 1px solid #ffeaa7;
        border-radius: 4px;
        padding: 10px;
        color: #856404;
        font-size: 12px;
        z-index: 1000;
        max-width: 300px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1);
      `;
      
      warningDiv.innerHTML = `
        <strong>📐 Layout Adjustment:</strong><br>
        ${warnings.join('<br>')}
      `;
      
      document.body.appendChild(warningDiv);
      
      // Remove after 3 seconds
      setTimeout(() => {
        if (warningDiv.parentNode) {
          warningDiv.parentNode.removeChild(warningDiv);
        }
      }, 3000);
    },
    
    selectElement(elementName) {
      this.selectedElement = elementName;
    },
    
    getElementStyle(elementName) {
      const field = this.fieldAdjustments[elementName];
      if (!field || field.x === undefined || field.y === undefined) {
        logger.warn(`Element ${elementName} not found in fieldAdjustments, using default`);
        // Return default style if field not found
        return {
          position: 'absolute',
          left: '0px',
          top: '0px',
          width: '100px',
          height: '25px',
          cursor: 'move',
          border: '1px dashed #ccc',
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          padding: '2px',
          borderRadius: '2px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 5,
          transition: 'all 0.2s ease',
          textAlign: 'center'
        };
      }
      
      return {
        position: 'absolute',
        left: `${field.x}px`,
        top: `${field.y}px`,
        width: `${field.width}px`,
        height: `${field.height}px`,
        cursor: 'move',
        border: this.selectedElement === elementName ? '2px solid #007bff' : '1px dashed #ccc',
        backgroundColor: this.selectedElement === elementName ? 'rgba(0, 123, 255, 0.1)' : 'rgba(255, 255, 255, 0.9)',
        padding: '2px',
        borderRadius: '2px',
        fontSize: `${field.fontSize}px`,
        fontWeight: '500',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: this.selectedElement === elementName ? 10 : 5,
        transition: 'all 0.2s ease',
        textAlign: 'center'
      };
    },
    
    // Field visibility control methods
    selectAllFields() {
      Object.keys(this.stubFieldVisibility).forEach(field => {
        this.stubFieldVisibility[field] = true;
      });
    },
    
    deselectAllFields() {
      Object.keys(this.stubFieldVisibility).forEach(field => {
        this.stubFieldVisibility[field] = false;
      });
    },
    
    resetFieldDefaults() {
      this.stubFieldVisibility = {
        vendorName: true,
        vendorPhone: true,
        vendorAddress: true,
        billNumber: true,
        billDate: true,
        billAmount: true,
        amountPaid: true,
        description: true,
        totalPayment: true
      };
    },
    
    getVisibleColumnsCount() {
      let count = 0;
      if (this.stubFieldVisibility.billNumber) count++;
      if (this.stubFieldVisibility.billDate) count++;
      if (this.stubFieldVisibility.billAmount) count++;
      if (this.stubFieldVisibility.description) count++;
      return count;
    },
    
    applyFieldAdjustments() {
      // Force re-render to update the UI
      this.$forceUpdate();
    },
    
    resetFieldToDefault(fieldName) {
      const defaults = {
        date: { fontSize: 12, width: 120, height: 18, x: 450, y: 30 },
        amount: { fontSize: 14, width: 120, height: 22, x: 480, y: 80 },
        payee: { fontSize: 14, width: 350, height: 22, x: 120, y: 80 },
        memo: { fontSize: 12, width: 250, height: 18, x: 80, y: 160 },
        amountInWords: { fontSize: 12, width: 450, height: 18, x: 20, y: 110 }
      };
      
      this.fieldAdjustments[fieldName] = { ...defaults[fieldName] };
      this.applyFieldAdjustments();
    },
    
    fitFieldToContent(fieldName) {
      // Auto-fit field based on typical content length
      const contentEstimates = {
        date: '12/31/2024',
        amount: '$1,234.56',
        payee: 'Vendor Company Name',
        memo: 'PAY-12345 Payment for services',
        amountInWords: 'One Thousand Two Hundred Thirty Four Dollars'
      };
      
      const sampleContent = contentEstimates[fieldName] || 'Sample Text';
      const estimatedWidth = sampleContent.length * 8; // Approximate character width
      
      this.fieldAdjustments[fieldName].width = Math.max(
        this.getFieldMinWidth(fieldName), 
        Math.min(this.getFieldMaxWidth(fieldName), estimatedWidth + 20)
      );
      this.fieldAdjustments[fieldName].height = Math.max(20, Math.min(35, this.fieldAdjustments[fieldName].fontSize + 8));
      
      this.applyFieldAdjustments();
    },
    
    centerFieldHorizontally(fieldName) {
      // Center the field horizontally in the check area
      const checkWidth = 600;
      const centerX = (checkWidth - this.fieldAdjustments[fieldName].width) / 2;
      this.fieldAdjustments[fieldName].x = Math.max(0, Math.min(600, centerX));
      this.applyFieldAdjustments();
    },
    
    getFieldDisplayName(fieldName) {
      const displayNames = {
        date: 'Date',
        amount: 'Amount',
        payee: 'Vendor Name',
        memo: ' Memo',
        amountInWords: 'Amount in Words'
      };
      return displayNames[fieldName] || fieldName;
    },
    
    getFieldIcon(fieldName) {
      const icons = {
        date: '📅',
        amount: '💲',
        payee: '👤',
        memo: '📝',
        amountInWords: '📄'
      };
      return icons[fieldName] || '📄';
    },
    
    getFieldMinWidth(fieldName) {
      const minWidths = {
        date: 60,
        amount: 50,
        payee: 100,
        memo: 80,
        amountInWords: 200
      };
      return minWidths[fieldName] || 50;
    },
    
    getFieldMaxWidth(fieldName) {
      const maxWidths = {
        date: 150,
        amount: 200,
        payee: 500,
        memo: 400,
        amountInWords: 600
      };
      return maxWidths[fieldName] || 200;
    },
    
    
    async loadInitialData() {
      try {
        this.loading = true;
        const companyId = this.$route.params.id;
        
        // Load vendors for the current company (same as VendorWriteCheck)
        const vendorData = await ApiService.getAllVendors(companyId);
        this.vendors = (Array.isArray(vendorData) ? vendorData : [])
          .filter(vendor => vendor.active !== false) // Only show active vendors
          .map(vendor => ({
            id: vendor.id,
            name: vendor.vendorName || vendor.name,
            companyId: vendor.companyId,
            address: vendor.address, // Fallback address field
            addresses: vendor.addresses || [], // Include addresses relationship from vendor service
            phone: vendor.phone,
            email: vendor.email
          }));
        
      } catch (error) {
        this.inlineErrorMessage = 'Failed to load initial data';
        logger.error('Error loading initial data:', error);
      } finally {
        this.loading = false;
      }
    },
    async onVendorChange() {
      if (!this.selectedVendor) {
        this.payBills = [];
        this.selectedBillId = '';
        this.inlineErrorMessage = '';
        return;
      }
      
      try {
        this.loading = true;
        this.inlineErrorMessage = '';
        
        // Load vendor pay bills for the selected vendor with records
        const response = await ApiService.get(`/vendor/pay-bill/vendor/${this.selectedVendor}?includeRecords=true`);
        
        const payBillsData = response.data?.payBills || response.data || [];
        
        if (!Array.isArray(payBillsData)) {
          this.payBills = [];
          this.inlineErrorMessage = 'Invalid data format received from server. Please try again.';
          return;
        }
        
        // Filter only Check payment method records where isWriteCheckDone is true but isPrintCheckDone is false
        const readyToPrintBills = payBillsData.filter(payBill =>
          isCheckPaymentMethod(payBill.paymentMethod) &&
          payBill.isWriteCheckDone === true && // Only show bills that have been written
          !payBill.isPrintCheckDone // Don't show already printed bills
        );
        
        this.payBills = readyToPrintBills.map(payBill => ({
          id: payBill.id,
          billNumber: payBill.no,
          billDate: payBill.date,
          billAmount: payBill.total,
          contactName: payBill.contactName,
          phone: payBill.phone,
          comments: payBill.comments,
          paymentMethod: payBill.paymentMethod,
          isWriteCheckDone: payBill.isWriteCheckDone,
          isPrintCheckDone: payBill.isPrintCheckDone,
          records: payBill.records || [], // Include VendorPayBillRecord details
          payFromAccount: payBill.payFromAccount || null, // Include account details
        }));
        
        // Clear any previous selection
        this.selectedBillId = '';
        
        // Clear any previous error messages on successful load
        this.inlineErrorMessage = '';
        
      } catch (error) {
        logger.error('Error loading vendor pay bills:', error);
        this.payBills = [];
        
        // Provide specific error messages based on error type
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const serverMessage = error.response.data?.message || error.response.data?.error;
          
          if (status === 404) {
            this.inlineErrorMessage = 'No pay bills ready for printing found for this vendor';
          } else if (status === 403) {
            this.inlineErrorMessage = "You do not have permission to access this vendor's pay bills.";
          } else if (status === 500) {
            this.inlineErrorMessage = 'Server error occurred while loading pay bills. Please try again later.';
          } else {
            this.inlineErrorMessage =
              serverMessage || `Failed to load vendor pay bills (Error ${status}). Please try again.`;
          }
        } else if (error.request) {
          // Network error
          this.inlineErrorMessage =
            'Network error: Unable to connect to server. Please check your internet connection.';
        } else {
          // Other error
          this.inlineErrorMessage = 'An unexpected error occurred while loading pay bills. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },
    async loadPrinters() {
      try {
        this.loadingPrinters = true;
        this.printerLoadMethod = '';
        let printersFound = false;
        
        // Method 1: Try Electron's printer API (if running in Electron)
        if (window.require && !printersFound) {
          try {
            const { webContents } = window.require('electron').remote;
            if (webContents && webContents.getPrinters) {
              const electronPrinters = webContents.getPrinters();
              if (electronPrinters && electronPrinters.length > 0) {
                this.availablePrinters = electronPrinters.map(printer => ({
                  id: printer.name,
                  name: `${printer.displayName || printer.name}${printer.isDefault ? ' (Default)' : ''}`,
                  isDefault: printer.isDefault,
                  status: printer.status,
                  electronPrinter: true
                }));
                printersFound = true;
                this.printerLoadMethod = `✅ Loaded ${this.availablePrinters.length} printers via Electron API`;
                logger.log('Loaded printers via Electron API:', this.availablePrinters.length);
              }
            }
          } catch (electronError) {
            logger.log('Electron printer API not available:', electronError.message);
          }
        }
        
        // Method 2: Try backend API to get system printers
        if (!printersFound) {
          try {
            const response = await ApiService.get('/system/printers');
            if (response.data && response.data.printers && response.data.printers.length > 0) {
              this.availablePrinters = response.data.printers.map(printer => ({
                id: printer.name || printer.id,
                name: `${printer.displayName || printer.name}${printer.isDefault ? ' (Default)' : ''}`,
                isDefault: printer.isDefault,
                status: printer.status,
                backendPrinter: true
              }));
              printersFound = true;
              this.printerLoadMethod = `✅ Loaded ${this.availablePrinters.length} printers via Backend API`;
              logger.log('Loaded printers via backend API:', this.availablePrinters.length);
            }
          } catch (backendError) {
            logger.log('Backend printer API not available:', backendError.message);
          }
        }
        
        // Method 3: Try Web Print API (experimental)
        if (!printersFound && 'navigator' in window && 'printing' in navigator) {
          try {
            // This is experimental and may not work in all browsers
            const printCapabilities = await navigator.printing.getPrinters();
            if (printCapabilities && printCapabilities.length > 0) {
              this.availablePrinters = printCapabilities.map((printer, index) => ({
                id: printer.id || `printer-${index}`,
                name: printer.name || `Printer ${index + 1}`,
                webApiPrinter: true
              }));
              printersFound = true;
              this.printerLoadMethod = `✅ Loaded ${this.availablePrinters.length} printers via Web Print API`;
              logger.log('Loaded printers via Web Print API:', this.availablePrinters.length);
            }
          } catch (webApiError) {
            logger.log('Web Print API not available:', webApiError.message);
          }
        }
        
        // Method 4: Try to get real system printers via system API
        if (!printersFound) {
          try {
            const response = await ApiService.get('/system/printers');
            if (response.data && response.data.printers && response.data.printers.length > 0) {
              // Filter out virtual printers and only show real printers
              const realPrinters = response.data.printers.filter(printer => {
                const name = printer.name.toLowerCase();
                // Exclude virtual printers
                const virtualPrinters = [
                  'pdf', 'xps', 'fax', 'preview', 'save as', 'print to', 
                  'microsoft print to pdf', 'microsoft xps', 'cups-pdf'
                ];
                return !virtualPrinters.some(virtual => name.includes(virtual));
              });
              
              if (realPrinters.length > 0) {
                this.availablePrinters = realPrinters.map(printer => ({
                  id: printer.name || printer.id,
                  name: `${printer.displayName || printer.name}${printer.isDefault ? ' (Default)' : ''}`,
                  isDefault: printer.isDefault,
                  status: printer.status,
                  realPrinter: true
                }));
                printersFound = true;
                this.printerLoadMethod = `✅ Loaded ${this.availablePrinters.length} real printers via System API`;
                logger.log('Loaded real printers via system API:', this.availablePrinters.length);
              }
            }
          } catch (systemError) {
            logger.log('System printer API not available:', systemError.message);
          }
        }
        
        // No fallback printers - only show real system printers
        if (!printersFound) {
          this.availablePrinters = [];
          this.printerLoadMethod = `❌ No real printers detected on this system`;
          logger.log('No real printers found - showing empty list');
        }
        
        // Sort printers: Default first, then alphabetically
        this.availablePrinters.sort((a, b) => {
          if (a.isDefault && !b.isDefault) return -1;
          if (!a.isDefault && b.isDefault) return 1;
          return a.name.localeCompare(b.name);
        });
        
        // Set default printer if none selected
        if (!this.selectedPrinter && this.availablePrinters.length > 0) {
          // Prefer the default printer if available
          const defaultPrinter = this.availablePrinters.find(p => p.isDefault);
          this.selectedPrinter = defaultPrinter ? defaultPrinter.id : this.availablePrinters[0].id;
        }
        
        logger.log('Final printer list:', this.availablePrinters);
        
      } catch (error) {
        logger.error('Error loading printers:', error);
        // No fallback printers - only real system printers
        this.availablePrinters = [];
        this.printerLoadMethod = `❌ Error loading printers: ${error.message}`;
        this.selectedPrinter = null;
      } finally {
        this.loadingPrinters = false;
      }
    },
    async refreshPrinters() {
      await this.loadPrinters();
    },
    
    canPrint(bill) {
      return bill.isWriteCheckDone && !bill.isPrintCheckDone;
    },
    onCheckSelection() {
      // Sync single selection with batch selection for backward compatibility
      if (this.selectedBillIds.length === 1) {
        this.selectedBillId = this.selectedBillIds[0];
      } else if (this.selectedBillIds.length === 0) {
        this.selectedBillId = '';
      }
      // Force reactivity update
      this.$forceUpdate();
    },
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.clearAllSelections();
      } else {
        this.selectAllChecks();
      }
    },
    selectAllChecks() {
      this.selectedBillIds = this.payBills
        .filter(bill => this.canPrint(bill))
        .map(bill => bill.id);
      this.onCheckSelection();
    },
    clearAllSelections() {
      this.selectedBillIds = [];
      this.selectedBillId = '';
      this.onCheckSelection();
    },
    getStatusClass(bill) {
      if (bill.isPrintCheckDone) return 'status-printed';
      if (bill.isWriteCheckDone) return 'status-ready';
      return 'status-pending';
    },
    getStatusText(bill) {
      if (bill.isPrintCheckDone) return 'Printed';
      if (bill.isWriteCheckDone) return 'Ready to Print';
      return 'Pending';
    },
    getCheckNumberForBill() {
      // For pre-printed checks, the check number is already printed on the form
        return 'PRE-PRINTED';
    },
    async loadPrintHistory() {
      try {
        this.loadingHistory = true;
        const companyId = this.$route.params.id;
        
        // Load recent print history using the new API
        const response = await ApiService.get(`/vendor/pay-bill/print-history/${companyId}?limit=20`);
        if (response.data && response.data.success && response.data.data) {
          this.printHistory = response.data.data.map(item => ({
            id: item.id,
            checkNumber: 'PRE-PRINTED',
            billNumber: item.no,
            vendorName: item.vendor?.vendorName || 'Unknown Vendor',
            amount: item.total,
            printedAt: item.printDate || item.updatedAt, // Use printDate if available, fallback to updatedAt
            printerName: item.printerName || 'Unknown',
            isVoided: item.isVoided || false,
            voidedAt: item.voidedAt,
            voidedBy: item.voidedBy
          }));
        }
      } catch (error) {
        logger.log('Could not load print history:', error.message);
        this.printHistory = [];
      } finally {
        this.loadingHistory = false;
      }
    },
    
    // Field Adjustment API Methods
    async loadFieldAdjustments() {
      try {
        const companyId = this.$route.params.id;
        const response = await CheckFieldAdjustmentService.getFieldAdjustments(companyId);
        
        if (response.success && response.data) {
          this.fieldAdjustments = response.data;
          this.hasAdjustmentsInDB = true;
          logger.log('Field adjustments loaded from database:', this.fieldAdjustments);
        } else {
          this.hasAdjustmentsInDB = false;
        }
      } catch (error) {
        logger.log('Could not load field adjustments, using defaults:', error.message);
        this.hasAdjustmentsInDB = false;
        // Keep default fieldAdjustments if loading fails
      }
    },
    
    async saveFieldAdjustments() {
      try {
        const companyId = this.$route.params.id;
        const response = await CheckFieldAdjustmentService.saveFieldAdjustments(
          companyId, 
          this.fieldAdjustments,
          'default',
          'Default check field adjustments'
        );
        
        if (response.success) {
          this.hasAdjustmentsInDB = true;
          logger.log('Field adjustments saved successfully');
        }
      } catch (error) {
        logger.error('Failed to save field adjustments:', error);
      }
    },
    
    // Debounced save to prevent too many API calls
    debouncedSave() {
      if (this.saveTimeout) {
        clearTimeout(this.saveTimeout);
      }
      this.saveTimeout = setTimeout(() => {
        this.saveFieldAdjustments();
      }, 1000); // Save after 1 second of inactivity
    },
    
    // Manual save/update method
    async saveOrUpdateAdjustments() {
      try {
        await this.saveFieldAdjustments();
        
        // Show success message
        this.showSuccessModal = true;
        this.successTitle = this.hasAdjustmentsInDB ? 'Updated Successfully' : 'Saved Successfully';
        this.successMessage = this.hasAdjustmentsInDB 
          ? 'Field adjustments have been updated in the database.' 
          : 'Field adjustments have been saved to the database.';
      } catch (error) {
        this.showErrorModal = true;
        this.errorTitle = 'Save Failed';
        this.modalErrorMessage = 'Failed to save field adjustments. Please try again.';
      }
    },
    
    // Edit mode methods
    toggleEditMode() {
      this.editMode = !this.editMode;
      if (!this.editMode) {
        // Exit edit mode - clear selection
        this.selectedElement = null;
      }
    },
    
    // Expand/collapse methods
    toggleRemittanceAdvice() {
      this.remittanceAdviceExpanded = !this.remittanceAdviceExpanded;
    },
    
    toggleCheckLayout() {
      this.checkLayoutExpanded = !this.checkLayoutExpanded;
    },
    
    resetAllToDefaults() {
      const defaults = {
        date: { fontSize: 12, width: 120, height: 18, x: 450, y: 30 },
                amount: { fontSize: 14, width: 120, height: 22, x: 460, y: 95 },
                payee: { fontSize: 14, width: 350, height: 22, x: 99, y: 97 },
                memo: { fontSize: 12, width: 250, height: 18, x: 70, y: 191 },
                amountInWords: { fontSize: 12, width: 450, height: 18, x: 108, y: 150 }
      };
      
      Object.keys(defaults).forEach(fieldName => {
        this.fieldAdjustments[fieldName] = { ...defaults[fieldName] };
      });
      
      this.$forceUpdate();
    },
    getHistoryStatusClass(historyItem) {
      if (historyItem.isVoided) return 'status-voided';
      return 'status-printed';
    },
    getHistoryStatusText(historyItem) {
      if (historyItem.isVoided) return 'Voided';
      return 'Printed';
    },
    async voidCheck(historyItem) {
      if (!confirm(`Are you sure you want to void check #${historyItem.checkNumber}? This action cannot be undone.`)) {
        return;
      }
      
      try {
        const response = await ApiService.post('/vendor/pay-bill/void-check', {
          billId: historyItem.id,
          reason: 'Voided by user',
          voidedBy: 'Current User'
        });
        
        if (response.data.success) {
          // Update local history
          const item = this.printHistory.find(h => h.id === historyItem.id);
          if (item) {
            item.isVoided = true;
            item.voidedAt = new Date().toISOString();
            item.voidedBy = 'Current User';
          }
          
          this.successTitle = 'Check Voided';
          this.successMessage = `Check #${historyItem.checkNumber} has been voided successfully.`;
          this.showSuccessModal = true;
        } else {
          throw new Error(response.data.message || 'Failed to void check');
        }
        
      } catch (error) {
        this.errorTitle = 'Void Failed';
        this.modalErrorMessage = error.message || 'Failed to void check.';
        this.showErrorModal = true;
      }
    },
    onCheckStyleChange() {
      // Handle check style change - update layout preview
      logger.log('Check style changed to:', this.checkStyle);
      // Force re-render of layout preview to apply new check style
      this.$forceUpdate();
    },
    onCheckPositionChange() {
      // Handle check position change
      logger.log('Check position changed to:', this.checkPosition);
      // Force re-render of layout preview to apply new check position
      this.$forceUpdate();
    },
    onPrinterChange() {
      // Handle printer selection change
      logger.log('Printer changed to:', this.selectedPrinter);
      // Future: Could load printer-specific settings or capabilities
    },
    goBack() {
      this.$router.push(`/company/${this.$route.params.id}/dashboard`);
    },
    closeSuccessModal() {
      this.showSuccessModal = false;
      this.successTitle = '';
      this.successMessage = '';
    },
    closeErrorModal() {
      this.showErrorModal = false;
      this.errorTitle = 'Error';
      this.modalErrorMessage = '';
    },

    // Print confirmation modal methods
    closePrintConfirmationModal() {
      this.showPrintConfirmationModal = false;
      this.printedBills = [];
      this.printedChecksCount = 0;
      this.updatingStatus = false;
    },

    async confirmPrintJobDone() {
      try {
        this.updatingStatus = true;

        // Mark all printed bills as printed
        for (const bill of this.printedBills) {
          await this.markCheckAsPrinted(bill.id);
        }

        // Close confirmation modal
        this.closePrintConfirmationModal();

        // Show success message
        this.successTitle = 'Status Updated';
        this.successMessage = `Successfully updated status for ${this.printedChecksCount} check(s). The checks are now marked as printed.`;
        this.showSuccessModal = true;

        // Reload the bills list to update status
        await this.onVendorChange();

      } catch (error) {
        logger.error('Error updating print status:', error);
        this.updatingStatus = false;
        
        this.errorTitle = 'Update Failed';
        this.modalErrorMessage =
          'Failed to update the print status. Please try again or contact support if the issue continues.';
        this.showErrorModal = true;
      }
    },

    printJobNotDone() {
      // Close confirmation modal without updating status
      this.closePrintConfirmationModal();
      
      // Show info message
      this.successTitle = 'Print Status Not Updated';
      this.successMessage = 'The print status has not been updated. You can try printing again or contact support if there were issues with the print job.';
      this.showSuccessModal = true;
    },

    // Test method for debugging modal
    testConfirmationModal() {
      this.printedBills = this.selectedPayBills.length > 0 ? this.selectedPayBills : [{ id: 'test', billNumber: 'TEST-001', billAmount: 100 }];
      this.printedChecksCount = this.printedBills.length;
      this.showPrintConfirmationModal = true;
      logger.log('Test confirmation modal triggered');
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: '2-digit'
        });
      } catch (error) {
        return '-';
      }
    },
    formatDateToUS(date) {
      if (!date) return '';
      try {
        const d = new Date(date);
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const year = d.getFullYear();
        return `${month}/${day}/${year}`;
      } catch (error) {
        return '';
      }
    },
    getCurrentDateUS() {
      // Always return current date in US format (MM/DD/YYYY)
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      return `${month}/${day}/${year}`;
    },
    cleanBillNumber(billNo) {
      if (!billNo) return '';
      
      // Remove vendor prefix pattern like "Sadhi-BILL-" or "VendorName-BILL-"
      // Pattern: remove everything up to and including "-BILL-"
      const cleanedBillNo = billNo.replace(/^.*?-BILL-/, '');
      
      return cleanedBillNo || billNo; // Return original if no pattern found
    },
    getVendorName(bill) {
      // Get vendor name from the selected vendor or from bill contact name
      const selectedVendor = this.vendors.find((v) => String(v.id) === String(this.selectedVendor));
      return selectedVendor?.name || bill.contactName || 'Unknown Vendor';
    },
    getVendorAddress() {
      // Get vendor address from the selected vendor's addresses relationship
      const selectedVendor = this.vendors.find((v) => String(v.id) === String(this.selectedVendor));
      
      if (selectedVendor && selectedVendor.addresses && selectedVendor.addresses.length > 0) {
        // Use the first address (or billing address if available)
        const billingAddress = selectedVendor.addresses.find(addr => addr.type === 'Billing') || selectedVendor.addresses[0];
        
        const addressParts = [];
        if (billingAddress.address1) addressParts.push(billingAddress.address1);
        if (billingAddress.address2) addressParts.push(billingAddress.address2);
        if (billingAddress.city) addressParts.push(billingAddress.city);
        if (billingAddress.state) addressParts.push(billingAddress.state);
        if (billingAddress.postalCode) addressParts.push(billingAddress.postalCode);
        if (billingAddress.country) addressParts.push(billingAddress.country);
        
        return addressParts.length > 0 ? addressParts.join(', ') : 'Address not available';
      }
      
      // Fallback to vendor.address field if addresses relationship is not loaded
      if (selectedVendor && selectedVendor.address) {
        return selectedVendor.address;
      }
      
      return 'Address not available';
    },
    getBankInfo(bill) {
      // Get bank info from pay bill's payFromAccount or use default
      if (bill.payFromAccount) {
        return {
          routingNumber: bill.payFromAccount.routingNumber || this.bankInfo.routingNumber,
          accountNumber: bill.payFromAccount.accountNumber || this.bankInfo.accountNumber
        };
      }
      return this.bankInfo;
    },
    getCheckMemo(bill) {
      // Clean the bill number by removing vendor prefix but keeping PAY- and what follows
      let cleanBillNumber = bill.billNumber || '';
      
      // Remove patterns like "Sadhi-" or "VendorName-" but keep "PAY-" and everything after
      cleanBillNumber = cleanBillNumber.replace(/^.*?-(PAY-.*)$/, '$1');
      
      // Get memo from individual bill records
      if (bill.records && bill.records.length > 0) {
        // Collect all unique memos from bill records
        const memos = bill.records
          .map(record => record.bill?.comments || record.bill?.memo || '')
          .filter(memo => memo && memo.trim() !== '')
          .filter((memo, index, arr) => arr.indexOf(memo) === index); // Remove duplicates
        
        if (memos.length > 0) {
          // Combine clean bill number with individual bill memos
          return `${cleanBillNumber} ${memos.join(', ')}`;
        }
      }
      
      // Fallback to bill comments with clean bill number
      if (bill.comments && bill.comments.trim() !== '') {
        return `${cleanBillNumber} ${bill.comments}`;
      }
      
      // Final fallback - just the clean bill number
      return cleanBillNumber || 'Payment';
    },
    formatCurrency(value, includeSymbol = true) {
      if (!value || isNaN(value)) return includeSymbol ? '$0.00' : '0.00';
      const formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
      }).format(value);
      // Return with or without dollar symbol based on parameter
      if (includeSymbol) {
        return formatted.startsWith('$') ? formatted : '$' + formatted;
      } else {
        return formatted.replace('$', '');
      }
    },
    numberToWords(num) {
      if (num === 0) return 'Zero Dollars';
      
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
      const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
      const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
      
      function convertHundreds(n) {
        let result = '';
        if (n >= 100) {
          result += ones[Math.floor(n / 100)] + ' Hundred ';
          n %= 100;
        }
        if (n >= 20) {
          result += tens[Math.floor(n / 10)] + ' ';
          n %= 10;
        } else if (n >= 10) {
          result += teens[n - 10] + ' ';
          return result;
        }
        if (n > 0) {
          result += ones[n] + ' ';
        }
        return result;
      }
      
      let dollars = Math.floor(num);
      const cents = Math.round((num - dollars) * 100);
      
      let result = '';
      
      if (dollars >= 1000000) {
        result += convertHundreds(Math.floor(dollars / 1000000)) + 'Million ';
        dollars %= 1000000;
      }
      if (dollars >= 1000) {
        result += convertHundreds(Math.floor(dollars / 1000)) + 'Thousand ';
        dollars %= 1000;
      }
      if (dollars > 0) {
        result += convertHundreds(dollars);
      }
      
      result += dollars === 1 ? 'Dollar' : 'Dollars';
      
      if (cents > 0) {
        result += ' and ' + cents + '/100';
      }
      
      return result.trim();
    },
    
    // Print Check functionality
    async printCheck() {
      if (!this.selectedPrinter) {
        this.showErrorModal = true;
        this.modalErrorMessage = 'Please select a printer before printing.';
        return;
      }

      if (this.selectedPayBills.length === 0) {
        this.showErrorModal = true;
        this.modalErrorMessage = 'Please select at least one check to print.';
        return;
      }

      try {
        this.loading = true;

        // Validate all checks before printing
        for (const bill of this.selectedPayBills) {
          this.validateCheckData(bill);
        }

        // Store printed bills for confirmation
        this.printedBills = [...this.selectedPayBills];
        this.printedChecksCount = this.selectedPayBills.length;

        // Print all selected checks
        let successfulPrints = 0;
        for (const bill of this.selectedPayBills) {
          try {
            await this.printSingleCheck(bill);
            successfulPrints++;
          } catch (error) {
            logger.error('Error printing check:', error);
            // Continue with other checks even if one fails
          }
        }

        // If no prints were successful, show error
        if (successfulPrints === 0) {
          throw new Error('All print jobs failed');
        }

        // Show confirmation modal after all prints are complete
        logger.log('All prints completed, showing confirmation modal. Successful prints:', successfulPrints);
        this.showPrintConfirmationModal = true;

        // Clear selections
        this.selectedBillIds = [];
        this.selectedBillId = '';

      } catch (error) {
        logger.error('Error printing checks:', error);
        
        // Show specific error message based on error type
        if (error.message === 'Check data validation failed') {
          // Validation error already shown in validateCheckData
          return;
        } else if (error.message.includes('Printing failed:')) {
          // Print-specific error (user cancelled, printer unavailable, etc.)
          this.showErrorModal = true;
          this.errorTitle = 'Printing Issue';
          
          // Convert technical error reasons to user-friendly messages
          const reason = error.message.replace('Printing failed: ', '');
          let userMessage = '';
          
          if (reason === 'cancelled') {
            userMessage = 'You cancelled the printing process. The check status has not been updated.';
          } else if (reason === 'likely_cancelled') {
            userMessage = 'The printing process was interrupted. The check status has not been updated.';
          } else if (reason === 'timeout') {
            userMessage = 'The printing process timed out. Please try again or check your printer connection.';
          } else if (reason === 'no_print_methods') {
            userMessage = 'No printing method is available. The PDF has been downloaded to your computer instead.';
          } else if (reason.includes('error')) {
            userMessage = 'There was a technical issue with printing. Please try again or contact support.';
          } else {
            userMessage = 'The printing process encountered an issue. Please try again.';
          }
          
          this.modalErrorMessage = userMessage;
        } else {
          // General error
          this.showErrorModal = true;
          this.errorTitle = 'Printing Issue';
          this.modalErrorMessage =
            'We encountered an issue while printing your checks. Please try again or contact support if the problem continues.';
        }
      } finally {
        this.loading = false;
      }
    },

    // Add preview validation method
    validatePreviewLayout() {
      const warnings = [];
      
      // Check if elements are positioned outside preview area
      const previewWidth = 600;
      const previewHeight = 280;
      
      Object.keys(this.fieldAdjustments).forEach(field => {
        const pos = this.fieldAdjustments[field];
        if (pos.x < 0 || pos.x > previewWidth - pos.width) {
          warnings.push(`⚠️ ${field} field is positioned outside preview width`);
        }
        if (pos.y < 0 || pos.y > previewHeight - pos.height) {
          warnings.push(`⚠️ ${field} field is positioned outside preview height`);
        }
      });
      
      // Check for overlapping elements
      const fields = Object.keys(this.fieldAdjustments);
      for (let i = 0; i < fields.length; i++) {
        for (let j = i + 1; j < fields.length; j++) {
          const field1 = fields[i];
          const field2 = fields[j];
          const pos1 = this.fieldAdjustments[field1];
          const pos2 = this.fieldAdjustments[field2];
          
          if (this.isOverlapping(pos1, pos2)) {
            warnings.push(`⚠️ ${field1} and ${field2} fields are overlapping`);
          }
        }
      }
      
      if (warnings.length > 0) {
        const warningMessage = 'Layout Preview Issues:\n\n' + warnings.join('\n') + '\n\nPlease adjust element positions for better PDF output.';
        alert(warningMessage);
      }
      
      return warnings.length === 0;
    },

    // Check if two elements are overlapping
    isOverlapping(pos1, pos2) {
      return !(pos1.x + pos1.width < pos2.x || 
               pos2.x + pos2.width < pos1.x || 
               pos1.y + pos1.height < pos2.y || 
               pos2.y + pos2.height < pos1.y);
    },

    async printSingleCheck(bill) {
      try {
        // Validate check data elements before generating PDF
        this.validateCheckData(bill);

        // Generate form data with custom coordinates from drag and drop adjustments
        const formData = {
          date: this.formatDateToUS(new Date()),
          payee: this.getVendorName(bill),
          amount: bill.billAmount, // Use billAmount instead of amount
          amountInWords: this.numberToWords(bill.billAmount),
          memo: this.getCheckMemo(bill),
          checkNumber: this.getCheckNumberForBill(),
          routingNumber: this.getBankInfo(bill).routingNumber,
          accountNumber: this.getBankInfo(bill).accountNumber,
          billRecords: bill.records || [],
          // Include custom coordinates from drag and drop adjustments
          customCoordinates: {
            amount: {
              x: this.fieldAdjustments.amount.x,
              y: this.fieldAdjustments.amount.y,
              width: this.fieldAdjustments.amount.width,
              height: this.fieldAdjustments.amount.height,
              fontSize: this.fieldAdjustments.amount.fontSize
            },
            payee: {
              x: this.fieldAdjustments.payee.x,
              y: this.fieldAdjustments.payee.y,
              width: this.fieldAdjustments.payee.width,
              height: this.fieldAdjustments.payee.height,
              fontSize: this.fieldAdjustments.payee.fontSize
            },
            memo: {
              x: this.fieldAdjustments.memo.x,
              y: this.fieldAdjustments.memo.y,
              width: this.fieldAdjustments.memo.width,
              height: this.fieldAdjustments.memo.height,
              fontSize: this.fieldAdjustments.memo.fontSize
            },
            amountInWords: {
              x: this.fieldAdjustments.amountInWords.x,
              y: this.fieldAdjustments.amountInWords.y,
              width: this.fieldAdjustments.amountInWords.width,
              height: this.fieldAdjustments.amountInWords.height,
              fontSize: this.fieldAdjustments.amountInWords.fontSize
            },
            date: {
              x: this.fieldAdjustments.date.x,
              y: this.fieldAdjustments.date.y,
              width: this.fieldAdjustments.date.width,
              height: this.fieldAdjustments.date.height,
              fontSize: this.fieldAdjustments.date.fontSize
            }
          },
          // Include stub field visibility settings
          stubFieldVisibility: this.stubFieldVisibility,
          showStubTitles: this.showStubTitles
        };

        logger.log('Printing check with custom coordinates:', formData.customCoordinates);

        // Generate PDF with custom coordinates
        const response = await ApiService.post('/system/generate-pdf-overlay', {
          formData: formData,
          templateType: this.checkStyle,
          customCoordinates: formData.customCoordinates
        }, {
          responseType: 'arraybuffer'
        });

        // Validate PDF response
        if (!response.data || response.data.length === 0) {
          throw new Error('PDF generation failed - no data received from server');
        }

        logger.log('PDF generated successfully, size:', response.data.length, 'bytes');

        // Print PDF and get result
        const printResult = await this.sendToPrinter(response.data, this.selectedPrinter);

        // Only proceed if printing actually succeeded
        if (printResult.success) {
          logger.log(`Print successful via ${printResult.method} for bill ${bill.id}`);
          // Don't mark as printed yet - wait for user confirmation
        } else {
          logger.log(`Print failed via ${printResult.method}, reason: ${printResult.reason} for bill ${bill.id}`);
          // Don't mark as printed - user cancelled or printing failed
          throw new Error(`Printing failed: ${printResult.reason || 'Unknown error'}`);
        }

      } catch (error) {
        logger.error('Error printing single check:', error);
        
        // Show user-friendly error alert
        this.showPDFErrorAlert(error);
        throw error;
      }
    },

    // Validate check data elements
    validateCheckData(bill) {
      const errors = [];
      
      // Check if essential data exists
      if (!bill.billAmount || bill.billAmount <= 0) {
        errors.push('• Bill amount is missing or invalid');
      }
      
      if (!this.getVendorName(bill) || this.getVendorName(bill) === 'Unknown Vendor') {
        errors.push('• Vendor name is missing');
      }
      
      if (!this.getCurrentDateUS()) {
        errors.push('• Date is missing');
      }
      
      // Check if coordinates are valid
      const requiredFields = ['amount', 'payee', 'date', 'memo', 'amountInWords'];
      requiredFields.forEach(field => {
        const pos = this.fieldAdjustments[field];
        if (!pos || pos.x === undefined || pos.y === undefined) {
          errors.push(`• ${field} field position needs to be adjusted`);
        }
      });
      
      if (errors.length > 0) {
        const errorMessage = 'Please fix the following issues before printing:\n\n' + errors.join('\n') + '\n\nYou can adjust the field positions by dragging them in the preview area.';
        
        // Show error modal instead of alert for better UX
        this.showErrorModal = true;
        this.errorTitle = 'Check Setup Required';
        this.modalErrorMessage = errorMessage;
        throw new Error('Check data validation failed');
      }
    },

    // Show user-friendly PDF error alert
    showPDFErrorAlert(error) {
      let errorTitle = 'Unable to Generate Check';
      let errorMessage = 'We encountered an issue while preparing your check for printing.\n\n';
      
      // Provide user-friendly messages based on error type
      if (error.response) {
        // Server error - show friendly message
        if (error.response.status === 500) {
          errorMessage += 'Our system is temporarily experiencing issues. Please try again in a few moments.';
        } else if (error.response.status === 404) {
          errorMessage += 'The requested service is not available. Please contact support.';
        } else {
          errorMessage += 'There was a problem with our server. Please try again or contact support if the issue continues.';
        }
      } else if (error.request) {
        // Network error - show friendly message
        errorMessage += 'Unable to connect to our servers. Please check your internet connection and try again.';
      } else {
        // Other error - show friendly message
        errorMessage += 'An unexpected issue occurred. Please try again or contact support if the problem persists.';
      }
      
      errorMessage += '\n\nWhat you can do:\n';
      errorMessage += '• Check your internet connection\n';
      errorMessage += '• Try refreshing the page\n';
      errorMessage += '• Contact support if the issue continues\n';
      errorMessage += '• Your check data has been saved and is safe';
      
      // Show error modal instead of alert for better UX
      this.showErrorModal = true;
      this.errorTitle = errorTitle;
      this.modalErrorMessage = errorMessage;
    },

    async sendToPrinter(pdfBuffer, printerId) {
      try {
        this.isPrinting = true;
        this.printStatus = 'Preparing print job...';
      logger.log('Sending PDF to printer:', printerId);
      logger.log('PDF size:', pdfBuffer.length, 'bytes');
      
        // Method 1: Try Electron printing (if running in Electron)
        if (window.require) {
          try {
            this.currentPrintMethod = 'Electron';
            this.printStatus = 'Sending to printer via Electron...';
            const { ipcRenderer } = window.require('electron');
            if (ipcRenderer) {
              // Send print job to Electron main process
              await ipcRenderer.invoke('print-pdf', {
                pdfBuffer: Array.from(pdfBuffer),
                printerId: printerId
              });
              this.printStatus = 'Print job sent successfully!';
              logger.log('Print job sent via Electron');
              return { success: true, method: 'Electron' };
            }
          } catch (electronError) {
            logger.log('Electron printing not available:', electronError.message);
          }
        }
        
        // Method 2: Try browser printing
        if (window.print) {
          try {
            this.currentPrintMethod = 'Browser';
            this.printStatus = 'Opening print dialog...';
            // Create blob from PDF buffer
            const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            
            // Open PDF in new window and print
            const printWindow = window.open(url, '_blank', 'width=800,height=600');
            if (printWindow) {
              return new Promise((resolve) => {
                printWindow.onload = () => {
                  this.printStatus = 'PDF loaded, starting print...';
                  // Wait for PDF to fully load
                  setTimeout(() => {
                    printWindow.print();
                    this.printStatus = 'Print dialog opened. Please complete printing...';
                    
                    let printCompleted = false;
                    let printCancelled = false;
                    
                    // Immediate fallback - assume print completed after 3 seconds if no cancellation
                    setTimeout(() => {
                      if (!printCompleted && !printCancelled) {
                        printCompleted = true;
                        // Don't close the window automatically - let user close it manually
                        // printWindow.close();
                        URL.revokeObjectURL(url);
                        this.isPrinting = false;
                        this.printStatus = 'Print job completed (immediate fallback) - Please close the print window manually.';
                        logger.log('Print job completed via immediate fallback - window left open for user to close');
                        resolve({ success: true, method: 'Browser' });
                      }
                    }, 3000);
                    
                    // Listen for afterprint event to know when printing is done
                    const handleAfterPrint = () => {
                      if (!printCompleted) {
                        printCompleted = true;
                        // Don't close the window automatically - let user close it manually
                        // printWindow.close();
                        URL.revokeObjectURL(url);
                        this.isPrinting = false;
                        this.printStatus = 'Print job completed! Please close the print window manually.';
                        logger.log('Print job completed via browser - window left open for user to close');
                        resolve({ success: true, method: 'Browser' });
                      }
                    };
                    
                    // Listen for beforeunload to detect when user manually closes the window
                    const handleBeforeUnload = () => {
                      if (!printCompleted && !printCancelled) {
                        printCancelled = true;
                        URL.revokeObjectURL(url);
                        this.isPrinting = false;
                        this.printStatus = 'Print window closed by user';
                        logger.log('Print window closed by user');
                        resolve({ success: false, method: 'Browser', reason: 'cancelled' });
                      }
                    };
                    
                    printWindow.addEventListener('afterprint', handleAfterPrint);
                    printWindow.addEventListener('beforeunload', handleBeforeUnload);
                    
                    // Fallback: check if window is still focused after print dialog
                    const checkPrintComplete = () => {
                      if (document.hasFocus() && !printCompleted && !printCancelled) {
                        // User came back to main window, check if printing was completed
                        setTimeout(() => {
                          if (!printCompleted && !printCancelled) {
                            // Likely cancelled if user returned without afterprint event
                            printCancelled = true;
                            printWindow.close();
                            URL.revokeObjectURL(url);
                            this.isPrinting = false;
                            this.printStatus = 'Print likely cancelled';
                            logger.log('Print job likely cancelled (user returned without afterprint)');
                            resolve({ success: false, method: 'Browser', reason: 'likely_cancelled' });
                          }
                        }, 2000);
                      } else if (!printCompleted && !printCancelled) {
                        // Still in print dialog, check again
                        setTimeout(checkPrintComplete, 1000);
                      }
                    };
                    
                    // Start checking after a delay (print dialog should be open)
                    setTimeout(checkPrintComplete, 2000);
                    
                    // Fallback timeout - show confirmation modal after 5 seconds if no afterprint event
                    setTimeout(() => {
                      if (!printCompleted && !printCancelled) {
                        // Assume print completed if no cancellation detected
                        printCompleted = true;
                        // Don't close the window automatically - let user close it manually
                        // printWindow.close();
                        URL.revokeObjectURL(url);
                        this.isPrinting = false;
                        this.printStatus = 'Print job completed (timeout fallback) - Please close the print window manually.';
                        logger.log('Print job completed via timeout fallback - window left open for user to close');
                        resolve({ success: true, method: 'Browser' });
                      }
                    }, 5000);
                  }, 1000);
                };
                
                // Handle window close event - user manually closes the window
                printWindow.addEventListener('beforeunload', () => {
                  URL.revokeObjectURL(url);
                  this.isPrinting = false;
                  this.printStatus = 'Print window closed by user';
                  logger.log('Print window closed by user');
                });
                
                logger.log('Print job sent via browser');
              });
            }
          } catch (browserError) {
            logger.log('Browser printing failed:', browserError.message);
            return { success: false, method: 'Browser', reason: 'error', error: browserError.message };
          }
        }
        
        // Method 3: Try backend printing service
        try {
          const response = await ApiService.post('/system/print-pdf', {
            pdfBuffer: Array.from(pdfBuffer),
            printerId: printerId,
            jobName: 'Vendor Check Print'
          });
          
          if (response.data.success) {
            logger.log('Print job sent via backend:', response.data.message);
            return { success: true, method: 'Backend' };
          } else {
            throw new Error(response.data.message || 'Backend printing failed');
          }
        } catch (backendError) {
          logger.log('Backend printing failed:', backendError.message);
          // Continue to next method instead of returning
        }
        
        // Method 4: Try iframe printing (better control)
        try {
          logger.log('Trying iframe printing method');
          const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          
          return new Promise((resolve) => {
            // Create hidden iframe
            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.src = url;
            document.body.appendChild(iframe);
            
            let printCompleted = false;
            
            iframe.onload = () => {
              setTimeout(() => {
                // Print the iframe content
                iframe.contentWindow.print();
                
                // Listen for afterprint event
                const handleAfterPrint = () => {
                  if (!printCompleted) {
                    printCompleted = true;
                    // Don't remove iframe automatically - let user close it manually
                    // document.body.removeChild(iframe);
                    URL.revokeObjectURL(url);
                    logger.log('Print job completed via iframe - iframe left open for user to close');
                    resolve({ success: true, method: 'Iframe' });
                  }
                };
                
                iframe.contentWindow.addEventListener('afterprint', handleAfterPrint);
                
                // Fallback timeout after 30 seconds
                setTimeout(() => {
                  if (!printCompleted) {
                    printCompleted = true;
                    // Don't remove iframe automatically - let user close it manually
                    // document.body.removeChild(iframe);
                    URL.revokeObjectURL(url);
                    logger.log('Iframe print timed out - iframe left open for user to close');
                    resolve({ success: true, method: 'Iframe' });
                  }
                }, 30000);
              }, 2000); // Wait for PDF to load
            };
          });
        } catch (iframeError) {
          logger.log('Iframe printing failed:', iframeError.message);
          // Continue to next method instead of returning
        }
        
        // Method 5: Download PDF as fallback
        logger.log('All printing methods failed, downloading PDF as fallback');
        const blob = new Blob([pdfBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `check-${Date.now()}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        // Show user message about download
        this.showErrorModal = true;
        this.errorTitle = 'PDF Downloaded';
        this.modalErrorMessage =
          'Direct printing is not available on your system. The check has been downloaded to your computer. You can open the PDF file and print it manually from your computer.';
        
        return { success: false, method: 'Download', reason: 'no_print_methods', message: 'PDF downloaded instead' };
        
      } catch (error) {
        logger.error('Error sending to printer:', error);
        return { success: false, method: 'Unknown', reason: 'error', error: 'A technical issue occurred during printing' };
      }
    },

    async markCheckAsPrinted(billId) {
      try {
        // Use the existing updatePrintStatus endpoint (similar to VendorWriteCheck approach)
        const response = await ApiService.post('/vendor/pay-bill/update-print-status', {
          billIds: [billId],
          checkNumbers: ['PRE-PRINTED'], // Since we're using pre-printed checks
          printerName: this.selectedPrinter || 'Unknown',
          printDate: new Date().toISOString()
        });
        
        if (response.data.success) {
          logger.log('Successfully marked check as printed:', response.data.message);
        } else {
          throw new Error(response.data.message || 'Failed to mark check as printed');
        }
      } catch (error) {
        logger.error('Error marking check as printed:', error);
        // Don't throw error here as the check was already printed
      }
    },




    
  }
}
</script>
